import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { FileDown } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { PermissionBit } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { ErrorPage } from "../../../app/error-page";
import { CertificationDisclaimer } from "../../../app/footer";
import { DocumentTitle } from "../../../app/layout/document-title";
import { PasswordPrompt } from "../../../app/password-prompt";
import logoPng from "../../../assets/sq_logo.png";
import { ageSexOf } from "../../../components/age-sex-badge";
import { Button } from "../../../components/ui-kit/button";
import { Spinner } from "../../../components/ui-kit/spinner";
import { hasAdminAccess, hasPermission } from "../../../constants/permissions";
import { getServerTodayISO } from "../../../utils/server-time";
import { assert } from "../../../utils/utils";
import { HeartCoreCharts } from "../heartcore-charts/heartcore-charts";
import {
  HeartcoreRange,
  HeartcoreVariant,
} from "../heartcore-charts/use-heartcore-chart-data";
import { Patient } from "../patient";
import { compliancePercents } from "./compliance-summary";
import { LoadAllDataButton } from "./patient-profile-chart-tabs";

/**
 * One report (DRI / LVFP / HeartCore, from `?tab=`) without the app header or
 * sidebar — opened in a new tab from the Trends tabs' "Open in new tab" button.
 * Lives outside MainLayout, so it does that layout's login step itself.
 */
export const TrendsWindowRoute = observer(function TrendsWindowRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const { id } = useParams<{ id: string }>();
  assert(id !== undefined, "Patient ID param not found");
  const patientId = parseInt(id);
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const type =
    tab && Object.keys(REPORT_TITLES).includes(tab)
      ? (tab as HeartcoreVariant)
      : null;
  const api = props.app.seerlinqApi;
  const [printing, setPrinting] = useState(false);
  const [range, setRange] = useState<HeartcoreRange>(REPORT_RANGE);
  const [period, setPeriod] = useState<string | null>(null);
  // Only the HeartCore report is meant to be shared; DRI and LVFP stay on screen.
  const canExport = type === "heartcore";

  // Always light, whatever the app's theme: the window is what gets printed
  // and shared, and the PDF must come out the same for everyone. A layout
  // effect, so not even the first frame paints dark. Nothing in the window can
  // toggle the theme back (the footer switch lives in MainLayout), and leaving
  // the window hands the app its own theme again.
  useLayoutEffect(() => {
    document.documentElement.classList.remove("dark");
    return () => props.app.setTheme();
  }, [props.app]);

  const patient = useMemo(
    () => props.app.initPatient(patientId),
    [patientId, props.app],
  );

  // Patient.init reads the permissions, so it has to wait for the login.
  useEffect(() => {
    void (async () => {
      await api.assumeLoggedIn();
      await patient.init();
    })();
  }, [api, patient]);

  // Lays the page out at paper width before handing it to the browser's print
  // dialog: the charts only redraw on a resize, which printing alone doesn't
  // reliably give them in time. Put back on `afterprint`, not when `print()`
  // returns: mobile browsers return before the dialog has read the page.
  const today = getServerTodayISO();
  const reportTitle = type ? t(REPORT_TITLES[type]) : "";
  const printTitle = `${patient.data?.full_name ?? patientId} – ${reportTitle} – ${today}`;
  const exportPdf = async () => {
    if (printing) return;
    const title = document.title;
    setPrinting(true);
    await chartsRedrawn();
    window.addEventListener(
      "afterprint",
      () => {
        document.title = title;
        setPrinting(false);
      },
      { once: true },
    );
    // The browser's default PDF filename.
    document.title = printTitle;
    window.print();
  };

  // The browser's own Cmd/Ctrl+P would print the screen layout — ruler,
  // CURRENT row, screen-width charts — so it goes through the export too.
  // No deps: re-bound each render, so the handler never sees a stale export.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        !canExport ||
        !(e.metaKey || e.ctrlKey) ||
        e.key !== "p" ||
        !patient.loaded
      ) {
        return;
      }
      e.preventDefault();
      void exportPdf();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  });

  // MainLayout's re-auth prompt: without it a 428 would wait forever.
  const prompt = api.promptPassword && <PasswordPrompt api={api} />;

  if (patient.error) {
    return (
      <ErrorPage
        title={t`Error loading patient`}
        message={patient.error.message}
      />
    );
  }

  if (!api.loggedIn || !patient.loaded) {
    return (
      <div className="grow flex items-center justify-center">
        {prompt}
        <Spinner />
      </div>
    );
  }

  // Each report needs the permission its old QuickLook button did.
  if (!type || !hasPermission(api.permissions, REPORT_PERMISSIONS[type])) {
    return (
      <ErrorPage
        title={t`Access denied`}
        message={t`You don't have permission to open this report.`}
      />
    );
  }

  // A bounded scroll container, so the charts' `scopedScroll` mode pins the
  // events bar to its top and the zoom bar to its bottom. In print it grows to
  // its full height instead, or only the first screenful would make the page.
  return (
    <div
      className="h-screen overflow-auto px-4 print:h-auto print:overflow-visible print:px-0"
      style={
        printing
          ? { width: PRINT_WIDTH, margin: "0 auto", padding: 0 }
          : undefined
      }
    >
      <style>
        {printCss(printTitle, `Copyright © ${today.slice(0, 4)} Seerlinq`)}
      </style>
      {prompt}
      <DocumentTitle app={props.app} />
      <img src={logoPng} alt="Seerlinq" className="mx-auto mt-6 w-[320px]" />
      <ReportHeader patient={patient} title={reportTitle} period={period} />
      {/* Mounting the charts submits the analysis task, which needs consent. */}
      {patient.consentOk() ? (
        <HeartCoreCharts
          patientId={patientId}
          variant={type}
          events={patient.events ?? []}
          // AFib is a section of the DRI report alone.
          canAfib={
            type === "dri" && hasPermission(api.permissions, PermissionBit.AFIB)
          }
          // The PPG table a DRI point links to lives under the admin-gated `/raw`.
          canOpenPpgs={hasAdminAccess(api.permissions)}
          range={range}
          asReport
          onPeriodChange={setPeriod}
          scopedScroll
          jumpBar
          printing={printing}
          jumpBarEnd={
            <>
              <LoadAllDataButton
                range={range}
                defaultRange={REPORT_RANGE}
                onToggle={() =>
                  setRange((r) => (r === "all" ? REPORT_RANGE : "all"))
                }
              />
              {canExport && (
                <Button
                  size="sm"
                  variant="primary"
                  icon={FileDown}
                  disabled={printing}
                  onClick={exportPdf}
                >
                  <Trans>Export as PDF</Trans>
                </Button>
              )}
            </>
          }
        />
      ) : (
        <p className="text-muted-foreground py-8 text-center">
          <Trans>Charts are unavailable — patient consent is missing.</Trans>
        </p>
      )}
      <CertificationDisclaimer className="mx-auto my-6 max-w-xl text-center text-xs" />
    </div>
  );
});

/** Each report's title, as its old QuickLook report had it. */
const REPORT_TITLES: Record<HeartcoreVariant, MessageDescriptor> = {
  dri: msg`Seerlinq HeartCore: DRI QuickLook`,
  lvfp: msg`Seerlinq HeartCore: LVFP QuickLook`,
  heartcore: msg`Seerlinq HeartCore report`,
};

// The old reports' plot range.
const REPORT_RANGE: HeartcoreRange = 12;

const REPORT_PERMISSIONS: Record<HeartcoreVariant, PermissionBit> = {
  dri: PermissionBit.ACCESS_DRI,
  lvfp: PermissionBit.ACCESS_LVFP,
  heartcore: PermissionBit.RUN_HEART_CORE_QUICKLOOK,
};

/**
 * Whose report and for when, as the old reports opened: the patient, the
 * report's title, the monitored period and — for those who see it in the
 * profile header — the compliance. The period follows the zoom, so it is what
 * the PDF covers.
 */
const ReportHeader = observer(function ReportHeader(props: {
  patient: Patient;
  title: string;
  /** The zoomed period; null while the charts load. */
  period: string | null;
}) {
  const { t } = useLingui();
  const { data, compliance } = props.patient;
  const { age, sexLetter } = data
    ? ageSexOf(data)
    : { age: null, sexLetter: "" };
  // "Jane Doe, 67y F", as on the old reports.
  const ageSex = [age === null ? "" : t`${age}y`, sexLetter]
    .filter(Boolean)
    .join(" ");
  return (
    <header className="my-5 text-center">
      <p className="text-2xl font-semibold">
        {[data?.full_name, ageSex].filter(Boolean).join(", ")}
      </p>
      <h1 className="mt-1 text-lg">{props.title}</h1>
      <dl className="mt-3 flex flex-wrap justify-center gap-x-8 gap-y-2">
        <div>
          <dt className="text-muted-foreground text-xs font-semibold uppercase">
            <Trans>Monitored period</Trans>
          </dt>
          <dd className="text-sm font-medium">{props.period ?? "—"}</dd>
        </div>
        {/* Fetched only with monitoring access, like the profile header's. */}
        {compliance && (
          <div>
            <dt className="text-muted-foreground text-xs font-semibold uppercase">
              <Trans>Compliance (last 30 days)</Trans>
            </dt>
            <dd className="text-sm font-medium">
              {compliancePercents(compliance).soft}%
            </dd>
          </div>
        )}
      </dl>
    </header>
  );
});

// The width the page is laid out at for print. A4 portrait less the @page
// margins below is 190mm, 718 CSS px — but Chrome's print dialog lays a page
// out at 4/3 of that and scales it down to fit, so a 718px layout fills only
// three quarters of the sheet. Laid out at the 4/3 width, it fills Chrome's
// sheet exactly; a browser that lays out at the plain paper width (and
// headless PDF rendering) sees the page overflow and shrinks it to fit, which
// fills the sheet just the same.
const PRINT_WIDTH = Math.round((718 * 4) / 3);

// Without color-adjust the browser drops backgrounds — the threshold bands,
// the age+sex pill — to save ink. Any margin box of our own replaces all of
// Chrome's print header and footer — its timestamp, the page URL — so the page
// numbers are restated here. JSON.stringify quotes and escapes like CSS does.
function printCss(header: string, footer: string) {
  return `
@page {
  size: A4 portrait;
  margin: 10mm;
  font-size: 8pt;
  color: var(--muted-foreground);
  @top-center { content: ${JSON.stringify(header)}; }
  @bottom-left { content: ${JSON.stringify(footer)}; }
  @bottom-right { content: counter(page) "/" counter(pages); }
}
@media print {
  body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
}
`;
}

// Resolves once every chart has been redrawn at its new width, and stayed so
// for two frames. The redraw is a cascade — the width change reaches each chart
// through a ResizeObserver, and ECharts repaints on its own next frame — whose
// length grows with the data, so a fixed number of frames can print charts
// still drawn at their old width. Capped, so a chart that never settles can't
// hold the export up.
async function chartsRedrawn(): Promise<void> {
  const deadline = performance.now() + 3000;
  let settledFrames = 0;
  while (settledFrames < 2 && performance.now() < deadline) {
    await new Promise((resolve) => requestAnimationFrame(resolve));
    const settled = [
      ...document.querySelectorAll<HTMLElement>("[_echarts_instance_]"),
    ].every((chart) => {
      const svg = chart.querySelector("svg");
      return !svg || Math.abs(svg.width.baseVal.value - chart.clientWidth) < 1;
    });
    settledFrames = settled ? settledFrames + 1 : 0;
  }
}
